import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationsContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, UserPlus, ThumbsUp, Smile } from 'lucide-react';
import TimeAgo from './TimeAgo';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function notificationMeta(n) {
  switch (n.type) {
    case 'follow':
      return {
        icon: <UserPlus className="w-3.5 h-3.5 text-terra" />,
        text: 'started following you',
        href: n.actor ? `/profile/${n.actor.username}` : null,
      };
    case 'review_helpful':
      return {
        icon: <ThumbsUp className="w-3.5 h-3.5 text-terra" />,
        text: n.book ? `found your review of "${n.book.title}" helpful` : 'found your review helpful',
        href: n.book ? `/books/${n.book.id}` : null,
      };
    case 'reaction_like':
      return {
        icon: <Smile className="w-3.5 h-3.5 text-terra" />,
        text: n.book ? `liked your reaction on "${n.book.title}"` : 'liked your reaction',
        href: n.book ? `/books/${n.book.id}` : null,
      };
    default:
      return { icon: <Bell className="w-3.5 h-3.5 text-terra" />, text: 'sent you a notification', href: null };
  }
}

export default function NotificationsDropdown({ onClose }) {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications();
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleClick = async (n) => {
    const { href } = notificationMeta(n);
    if (!n.read) await markRead([n.id]);
    onClose();
    if (href) navigate(href);
  };

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-80 bg-cream border border-cream-dark rounded-2xl shadow-2xl z-[200] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
        <h3 className="font-heading text-base text-ink">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-terra hover:text-terra/80 transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="space-y-1 p-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-2 py-2.5">
                <Skeleton className="w-8 h-8 rounded-full bg-cream-dark flex-shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <Skeleton className="h-3 w-full bg-cream-dark" />
                  <Skeleton className="h-3 w-20 bg-cream-dark" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-10 px-4">
            <Bell className="w-8 h-8 text-ink-muted mx-auto mb-2 opacity-40" />
            <p className="text-sm text-ink-muted">No notifications yet.</p>
            <p className="text-xs text-ink-muted mt-1">When people follow you or interact with your reviews, you'll see it here.</p>
          </div>
        ) : (
          <ul>
            {notifications.map((n) => {
              const { icon, text, href } = notificationMeta(n);
              return (
                <li key={n.id}>
                  <button
                    onClick={() => handleClick(n)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-cream-dark/60 ${!n.read ? 'bg-terra/5' : ''}`}
                  >
                    {/* Unread dot */}
                    <div className="relative flex-shrink-0 mt-0.5">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={n.actor?.avatar_url} alt={n.actor?.display_name} />
                        <AvatarFallback className="bg-terra text-cream text-xs font-semibold">
                          {getInitials(n.actor?.display_name || n.actor?.username)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-cream border border-cream flex items-center justify-center">
                        {icon}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-ink leading-snug">
                        <span className="font-semibold">{n.actor?.display_name || n.actor?.username || 'Someone'}</span>
                        {' '}{text}
                      </p>
                      <TimeAgo date={n.created_at} className="text-xs text-ink-muted mt-0.5 block" />
                    </div>

                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-terra flex-shrink-0 mt-1.5" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
